/**
 * Core / Mint: Mint Sections
 * ------------------------------------------------------------------------------
 * - Uses Webpack dynamic imports to auto-import  associated `section.[name].js`
 *   files for each named section on the current page.
 * - Supports .js and .vue input files
 *
 * @namespace MintSections
 */
import { createApp } from 'vue'

import * as Mint from '~/core/mint/mint-setup'
import CoreComputed from '~/core/mint/methods/core-computed'
import SectionMethods from '~/core/mint/methods/section-methods'
import SectionMounted from '~/core/mint/methods/section-mounted'

import { handleToPascalCase } from '~/helpers/strings'

const attributes = {
  component: 'data-mint-section-component',
  section: 'data-mint-section',
  sectionId: 'data-section-id',
  skeleton: 'data-mint-section-skeleton',
}

/**
 * Create a new MintSections object.
 */
export default () => {

  /**
   * State.
   */
  const sectionApps = {}

  /**
   * Elements.
   */
  const nodeSelectors = {
    sections: [...document.querySelectorAll(`[${attributes.section}]`)],
  }

  /**
   * Run the setup for a section.
   * - Import with Webpack
   * - Returns an object.
   * @param {HTMLElement} section - [data-mint-section] element.
   *
   * Returns: handle of the section.
   * Returns: Module of the section.
   * Returns: HTMLObject of the Vue component, if applicable.
   * @returns {Object}
   */
  async function setupSection({ section }) {
    const handle = section.getAttribute(attributes.section)
    const vueElement = section.querySelector(`[${attributes.component}]`)

    const module = await import(
      /* webpackChunkName: 'section.[request]' */
      /* webpackExclude: /\_schema.js$/ */
      /* webpackInclude: /\.(vue|js)$/, */
      `~sections/${handle}/${handle}`
    )

    return {
      handle,
      module,
      vueElement,
    }
  }

  /**
   * Initialise section functionality.
   */
  async function init() {

    /**
     * Prevent execution if no sections are present and the user is not in Customiser.
     */
    if (!Shopify.designMode && !nodeSelectors.sections.length) {
      return
    }

    if (mint.flags.dev.disableVueSections) {
      window.console.log('[Mint] The mounting of Vue sections is disabled in theme-flags.liquid')
      return
    }

    /**
     * Loop over each section
     * - Import via Webpack
     * - Initiate Javascript functions
     * Loops using Promise.all() to not block the main thread and improve performance.
     */
    const sectionPromises = nodeSelectors.sections.map(async (section, index) => {
      const sectionModule = await setupSection({ section })

      initSection({
        section,
        sectionModule,
      })

      return sectionModule
    })

    await Promise.all(sectionPromises).then(() => {
      setDesignMode()
    })
  }

  /**
   * Initiate the Javascript of a given section.
   * - Vue sections will execute .mount()
   * - Javascript components will execute .init()
   * @param {HTMLElement} section - [data-mint-section] element.
   * @param {Module} sectionModule - Module Export of a given component.
   */
  function initSection({ section, sectionModule }) {
    const {
      handle,
      module,
      vueElement,
    } = sectionModule

    if (mint.flags.dev.enableDebug) {
      window.console.log('[Mint] Initialise section', {
        handle, module, vueElement,
      })
    }

    const SectionComponent = module.default

    /**
     * If this is a Vue-based section, create the app.
     */
    if (vueElement) {
      handleCreateVueApp({ SectionComponent, handle, section, vueElement })
      return
    }

    /**
     * Otherwise, do nothing for vanilla components. Initialisation
     * should be handled in its own file.
     */
    if (
      typeof SectionComponent === 'function' &&
      typeof SectionComponent().init === 'function'
    ) {
      SectionComponent({ section }).init()
    }
  }

  /**
   * Handle the auto-mounting of a Vue app.
   * - Ensures that naming conventions, Pinia, plugins et al is synced.
   * @param {Module} sectionComponent
   * @param {String} handle
   * @param {HTMLElement} section
   * @param {HTMLElement} vueElement
   */
  function handleCreateVueApp({ SectionComponent, handle, section, vueElement }) {
    if (mint.flags.dev.disableVueSections) {
      return
    }

    const components = {}
    components[handle] = SectionComponent

    /**
     * Vue returns 'Maximum Call Stack Error' if the name is *only* the
     * returned function string. One to investigate.
     */
    const PascalName = handleToPascalCase(handle)
    const name = `${PascalName}Section`

    const app = createApp({
      name,
      delimiters: ['${', '}'],
      components,
      computed: {
        ...CoreComputed(),
      },
      mounted() {
        SectionMounted(this)
      },
      methods: SectionMethods(),
    })

    /**
     * Add setup options.
     */
    Mint.setDirectives(app)
    // Mint.setMixins(app)

    Mint.setPlugins(app)

    /**
     * Handle mount.
     */
    const skeleton = section.querySelector(`[${attributes.skeleton}]`)
    const sectionId = section.getAttribute(attributes.sectionId)

    sectionApps[sectionId] = app

    if (skeleton) {
      section.replaceChild(vueElement, skeleton)
    }

    sectionApps[sectionId].mount(section)
  }

  /**
   * Handle app support within the Customiser.
   * - If a .vue section is moved/changed, remount so that changes
   *   are reflected in the Customiser.
   *
   * `event.target` will be the div with the class
   * `shopify-section` that Shopify wraps sections in.
   */
  function setDesignMode() {
    if (!Shopify.designMode) {
      return
    }

    /**
     * Admin has:
     * - Added new section
     * - Updated content in the Customiser
     */
    document.addEventListener('shopify:section:load', async (event) => {
      const section = event.target.querySelector(`[${attributes.section}]`)

      if (!section) {
        return
      }

      const sectionModule = await setupSection({ section })

      initSection({
        section,
        sectionModule,
      })

      mint.EventBus.dispatch('mint:customiser-event', {
        event: 'shopify:section:load',
        detail: event,
      })
    })

    /**
     * Admin has removed a section.
     */
    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target.querySelector(`[${attributes.section}]`)

      if (!section) {
        return
      }

      const sectionId = section.getAttribute(attributes.sectionId)

      if (Object.keys(sectionApps).includes(sectionId)) {
        sectionApps[sectionId].unmount(section)
      }

      mint.EventBus.dispatch('mint:customiser-event', {
        event: 'shopify:section:unload',
        detail: event,
      })
    })
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}